import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks';
import styled, { css } from 'styled-components';
import {
  formatCurrency,
  getTrimmedString,
} from 'components/project/ClerkScreen/shared/utils/common';
import { Icon } from 'components/project/ClerkScreen';
import {
  getProductAttribute, getAttributeItem, getCrJsonProp, getCrScore,
} from '../../../../stores/utils';
import { breakpoints } from '../../../../shared/utils/styles';
import {
  Container,
  Track,
  TrackKey,
  TrackValue,
  PrivateCommentsContainer,
  PublicCommentsContainer,
} from './Styles';

const KeyValueTrack = ({ label, value }) => (
  <Track>
    <TrackKey>{label}</TrackKey>
    <TrackValue>{value}</TrackValue>
  </Track>
);

KeyValueTrack.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const ItemDetailsContainer = () => {
  const {
    BidStore: { auctionProduct, auction },
    UIStore: { handleOpenPrivateNotes, handleOpenPublicComments },
  } = useStores();

  const attributes = auctionProduct?.attributes;
  const { auctioneerOpenPrivateNotes, auctioneerOpenPublicComments } = auction;

  const getAttribute = (key) => getProductAttribute(auctionProduct, key, '-');
  const getProductDetail = (key) => (auctionProduct && auctionProduct[key]
    ? getTrimmedString(auctionProduct[key])
    : '-');
  const getProductDetailCurrencyValue = (key) => {
    const value = getProductDetail(key);
    if (value !== '-') {
      return `$${formatCurrency(value)}`;
    }
    return value;
  };

  const hasValue = (value) => value !== '-';
  const keyValueToArray = (value, delimiter) => {
    if (!value) return null;
    return value.split(delimiter);
  };

  const getInspectionHeaderKeyValues = (valueToParse) => {
    if (!valueToParse) return { keys: null, values: null };
    const parts = keyValueToArray(valueToParse, ',');
    const keys = [];
    const values = [];
    parts.forEach((keyValue) => {
      const [key, value] = keyValueToArray(keyValue, '=');
      keys.push(getTrimmedString(key));
      values.push(getTrimmedString(value));
    });
    return { keys: keys.join(', '), values: values.join(', ') };
  };

  const subjectToMin = getAttributeItem('AuctionItemDNS', attributes);
  const formattedSubjectToMin = formatCurrency(subjectToMin?.value);
  const bookValue = getProductDetailCurrencyValue('bookValue');
  const targetValue = getProductDetailCurrencyValue('target');
  const reserve = getProductDetailCurrencyValue('reserve');
  const reserveType = getProductDetail('reserveType');

  const getFormattedReserve = () => {
    const reserveNumber = Number(reserve.replace(/\D/g, ''));
    if (reserveNumber === 0 && reserveType !== 'No Reserve') return '-';
    if (reserveNumber === 0 && reserveType === 'No Reserve') return 'No Reserve';
    if (reserveNumber > 0 && reserveType !== '-') return `${reserve} (${reserveType})`;
    return reserve;
  };
  const formattedDoc = getAttribute('Documents');
  const cr = getAttributeItem('ConditionReport', attributes);
  const crTotalScore = getCrJsonProp(cr, 'TotalScore');
  const crCosmeticScore = getCrJsonProp(cr, 'CosmeticScore');
  const crMechanicalScore = getCrJsonProp(cr, 'MechanicalScore');
  const crScore = getCrScore(crTotalScore, crMechanicalScore, crCosmeticScore);
  const inspectionHeader = getAttributeItem('InspectionHeader', attributes);
  const inspectionKeyValues = getInspectionHeaderKeyValues(inspectionHeader?.value);
  const inspectionHeaderKeys = inspectionKeyValues?.keys;
  const inspectionHeaderValues = inspectionKeyValues?.values;
  const mileageAttr = getAttribute('Mileage');
  const miles = isNaN(mileageAttr) ? mileageAttr : formatCurrency(mileageAttr);

  const top2Proxies = auctionProduct?.top2ProxyBids?.length
    ? auctionProduct.top2ProxyBids.map((item, index) => (
      <div key={index}>
        $
        {formatCurrency(item.proxyBid)}
        {' '}
        -
        {' '}
        {item.authorizedMember}
      </div>
    ))
    : 'No Proxy Bids';
  const publicComments = getProductDetail('publicComments');
  const formattedPublicComments = hasValue(publicComments)
    ? publicComments
    : 'No comments';
  const privateComments = getProductDetail('privateComments');
  const formattedPrivateComments = hasValue(privateComments)
    ? privateComments
    : 'No comments';

  return (
    <>
      <ReserveContainer>
        <div>
          <div>RESERVE:</div>
          <div>{getFormattedReserve()}</div>
        </div>
        <div>
          <div>S/T MIN:</div>
          <div>
            $
            {formattedSubjectToMin}
          </div>
        </div>
        <div>
          <div>Proxies:</div>
          <ProxyValues singleValue={auctionProduct?.top2ProxyBids?.length < 2}>
            {top2Proxies}
          </ProxyValues>
        </div>
      </ReserveContainer>
      <Container>
        {/* {auction.sellerAccount && (
          <KeyValueTrack label="Seller:" value={`${auction.sellerAccountName} (${auction.sellerAccount})`} />
        )} */}
        <KeyValueTrack label="CR:" value={crScore} />
        <KeyValueTrack label={`${inspectionHeaderKeys ?? 'Sub Scores'}:`} value={inspectionHeaderValues ?? 'Not Available'} />
        <KeyValueTrack label="MI/HR:" value={miles} />
        <KeyValueTrack label="Book:" value={bookValue} />
        <KeyValueTrack label="Target:" value={targetValue} />
        {/* <KeyValueTrack label="Proxies:" value={top2Proxies} /> */}
        <KeyValueTrack label="Docs:" value={formattedDoc} />
      </Container>
      <CommentContainer>
        <PublicCommentsContainer>
          <div>
            <div>Public Comments:</div>
            {!auctioneerOpenPublicComments.show && formattedPublicComments !== 'No comments' && (
              <Icon
                icon="external-link"
                size={1.5}
                onClick={() => handleOpenPublicComments(true, formattedPublicComments)}
              />
            )}
          </div>
          <div>{formattedPublicComments}</div>
        </PublicCommentsContainer>
        <PrivateCommentsContainer>
          <div>
            <div>Private Notes:</div>
            {!auctioneerOpenPrivateNotes.show && formattedPrivateComments !== 'No comments' && (
              <Icon
                icon="external-link"
                size={1.5}
                onClick={() => handleOpenPrivateNotes(true, formattedPrivateComments)}
              />
            )}
          </div>
          <div>{formattedPrivateComments}</div>
        </PrivateCommentsContainer>
      </CommentContainer>
    </>
  );
};

export const FlexColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

export const ProxyValues = styled.div`
  ${({ singleValue }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-size: ${singleValue ? 'min(1.5vw, 1em)' : 'min(1.3vw, .8em);'};
    @media (max-width: ${breakpoints.auctioneer.small}) {
      font-size: ${singleValue ? '1em' : '1em'};
    };
  `} 
`;

export const ReserveContainer = styled(FlexColumnContainer)`
grid-area: reserveAmt;
background-color: #BCEAFD;
justify-content: center;
font-size: min(1.5vw, 2em);
@media (max-width: ${breakpoints.auctioneer.small}) {
  font-size: .9em;
}
font-weight: normal;
text-transform: none;
width: 99%;
> div:not(:last-child) {
  border-bottom: 2px solid gray;
}
> div {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 0px;
  @media (max-width: ${breakpoints.auctioneer.small}) {
    padding: 5px 0px;
  }
  width: inherit;
  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: inherit;
    padding: 3px;
    font-weight: bold;
    :first-child {
      display: flex;
      justify-content: flex-end;
      width: 40%;
    }
    :last-child {
      padding-left: 5px;
      width: 60%;
    }
  }  
}
`;

export const CommentContainer = styled(FlexColumnContainer)`
  grid-area: comments;
  border: 1px solid gray;
  padding: 5px;
  > div {
    font-size: 2em;
    @media (max-width: ${breakpoints.auctioneer.small}) {
      font-size: 1.1em;
    }
    margin: 5px 0px;
  }
`;


export default observer(ItemDetailsContainer);
